import { delay } from 'redux-saga';
import { all, apply, call, put, select, takeEvery } from 'redux-saga/effects';

import * as actions from '../actions';
import * as api from '../actions/api';
import { getConvo } from '../selectors/conversation';
import { getMessages, getVisitor } from '../selectors/conversation';
import { getSettings } from '../selectors/settings';
import * as a from '../typedefs/actions';
import * as actionTypes from '../typedefs/actionTypes';
import { ConversationSerialized, PreMessage, TranscriptPayload } from '../typedefs/api';
import * as s from '../typedefs/store';
import { instance as LocalStorageManager } from '../util/LocalStorageManager';
import { sendDomEvent } from '../util/index';

function* sendMessage(action: a.SendMessageAction): Generator {
    try {
        const response = yield call(api.sendMessage, action.payload.text);
        yield put(
            actions.messageSent(action.payload.tempId, response.data.id, response.data.timestamp),
        );
    } catch (e) {
        yield put(actions.messageSendError(action.payload.tempId));
    }
}

/**
 * Real conversation is started via a websocket message (most likely escalated by Watson)
 */
function* escalatedToHumanSaga(action: a.StartedRealConvoSocketAction) {
    try {
        let messages: { data: TranscriptPayload };
        let conversationDetails: { data: { conversation: ConversationSerialized } };
        [messages, conversationDetails] = yield all([
            call(api.getTranscript),
            call(api.getConversationDetails),
        ]);

        yield put(actions.realConvoStarted());

        yield put(
            actions.convoDetailsFetched(
                conversationDetails.data.conversation,
                action.payload.visitorId,
                action.payload.visitorToken,
                messages.data.transcript,
            ),
        );

        yield call(saveConvoToLS, action.payload.visitorId, action.payload.visitorToken);
        yield put(actions.requestMarkRead());

        yield call(sendDomEvent, { action: 'human_conversation_created' });
    } catch (e) {
        // go to leave a message form
        yield put(actions.convoStartError());
    }
}

/**
 * Called when a visitor enters a page and localStorage has knowledge of an existing conversation
 */
export function* resumeConversationSaga() {
    try {
        // initialize the API
        const visitor: s.VisitorState = yield select(getVisitor);
        yield call(api.initApi, visitor.visitorToken, visitor.channel);

        // fetch conversation details
        let messages: { data: TranscriptPayload };
        let conversationDetails: { data: { conversation: ConversationSerialized } };
        [messages, conversationDetails] = yield all([
            call(api.getTranscript),
            call(api.getConversationDetails),
        ]);

        yield put(
            actions.convoDetailsFetched(
                conversationDetails.data.conversation,
                visitor.visitorId,
                visitor.visitorToken,
                messages.data.transcript,
            ),
        );

        yield put(actions.convoResumed());

        if (conversationDetails.data.conversation.isEnded) {
            yield call(convoEndedSaga);
            return;
        }

        yield call(
            api.sendMessage,
            `Visitor navigated to ${window.location.href}`,
            'visitor-url',
            'presence',
        );
    } catch (e) {
        // error restarting existing bot convo, so setup the state like we don't have one
        yield call(resetToFresh);
    }
}

function* resetToFresh() {
    const manager: typeof LocalStorageManager = yield apply(
        LocalStorageManager,
        LocalStorageManager.addData,
        [
            {
                hasConvo: false,
                visitorId: null,
                visitorToken: null,
                channel: null,
            },
        ],
    );
    const localData = yield apply(manager, manager.getData);
    yield put(actions.setLocalSettings(localData));
}

function* endConvoSaga() {
    yield put(actions.endConvoPending());

    try {
        yield call(api.endConvo);
    } catch (e) {
        // even though there was an error ending, the visitor doesn't care, so we'll treat it as ended from their perspective
    }

    yield call(convoEndedSaga);
    yield call(sendDomEvent, { action: 'ended' });
}

function* convoEndedSaga() {
    const convo: s.ConversationState = yield select(getConvo);
    if (convo.active) yield put(actions.endConvoSuccess());

    // remove the conversation from LS, but don't hide it so the survey can be presented
    yield put(actions.removeConversation(false));
}

function* saveConvoToLS(visitorId: number, visitorToken: string) {
    const manager: typeof LocalStorageManager = yield apply(
        LocalStorageManager,
        LocalStorageManager.addData,
        [
            {
                hasConvo: true,
                visitorId,
                visitorToken,
            },
        ],
    );
    const localData = yield apply(manager, manager.getData);
    yield put(actions.setLocalSettings(localData));
}

function* saveMinimizedState() {
    const convo: s.ConversationState = yield select(getConvo);
    yield apply(LocalStorageManager, LocalStorageManager.addData, [{ minimized: convo.minimized }]);
}

function* sendSurveyScore(action: a.SetSurveyScoreAction) {
    try {
        yield put(actions.surveyCompleted());
        yield call(api.saveSurveyScore, action.payload.score);
    } catch (e) {
        // ignore survey score errors
    }
}

function* hideBanner() {
    yield apply(LocalStorageManager, LocalStorageManager.addData, [{ hideBanner: true }]);
}

function* requestMarkRead() {
    const visitor: s.VisitorState = yield select(getVisitor);

    const convo: s.ConversationWithMetadata = yield select(getConvo);
    if (convo.minimized) return;

    const messages: s.MessageWithMetadata[] = yield select(getMessages);
    const unreadMessageIds = messages
        .filter(message => !message.readByMe)
        .map(message => message.id);
    if (unreadMessageIds.length === 0) return;

    try {
        interface Response {
            result: {
                successes: number[];
                errors: number[];
            };
        }
        const result: { data: Response } = yield call(api.markMessagesRead, unreadMessageIds);
        yield put(
            actions.messagesMarkedRead(result.data.result.successes, 'visitor', visitor.visitorId),
        );
    } catch (e) {
        // ignore error marking read
    }
}

/**
 * Remove the existance of an existing convo from local storage
 */
function* removeConversationFromLS() {
    yield apply(LocalStorageManager, LocalStorageManager.addData, [
        { hasConvo: false, minimized: false },
    ]);
}

function* agentAccepted(action: a.AgentAcceptedAction) {
    yield call(sendDomEvent, {
        action: 'accepted',
        acceptedBy: {
            avatar: action.payload.avatar,
            firstName: action.payload.firstName,
            lastName: action.payload.lastName,
            userId: action.payload.userId,
        },
    });
}

export default function* conversationSaga() {
    yield takeEvery(actionTypes.SEND_MESSAGE, sendMessage);
    yield takeEvery(actionTypes.SOCKET_EVENT_STARTED_REAL_CONVO, escalatedToHumanSaga);
    yield takeEvery(actionTypes.END_CONVO, endConvoSaga);
    yield takeEvery(actionTypes.SOCKET_EVENT_CONVERSATION_ENDED, convoEndedSaga);
    yield takeEvery(actionTypes.TOGGLE_MINIMIZED, saveMinimizedState);
    yield takeEvery(actionTypes.SET_SURVEY_SCORE, sendSurveyScore);
    yield takeEvery(actionTypes.HIDE_BANNER, hideBanner);
    yield takeEvery(actionTypes.REQUEST_MARK_READ, requestMarkRead);
    yield takeEvery(actionTypes.REMOVE_CONVERSATION, removeConversationFromLS);
    yield takeEvery(actionTypes.SOCKET_EVENT_AGENT_ACCEPTED, agentAccepted);
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/sagas/conversationSaga.ts